/**
 * Fuentes por capa (PDF interno equipo, no público cliente):
 *
 * | Bloque | Tras Guardar (snapshot) | Tras recarga / panel |
 * |--------|------------------------|----------------------|
 * | Parcela, núcleo, municipio, ha, tipo predio | `parcelaMeta` (vista técnica) | Igual |
 * | Estado gestión, riesgo (columna) | Form + POST | `unmasked.gestion` o hidratar + `parcelaMeta` |
 * | PII titular, teléfono, dirección, folios | Form en memoria | `GET /v1/captura` (AAL2) o datos enmascarados |
 * | Colindancias, modalidad, situación legal, condiciones, postura… | `gestiones.notas` JSON | Parse de `notas` (RLS o BFF) |
 * | Narrativas (4 campos) | `notas.narrativa` + form | Parse; fallback primera fila `visitas.narrativa` |
 * | Irregularidad (texto libre) | Form | Primera irregularidad activa API |
 * | Expediente checklist | `done` + catálogo docs | Estado actual del wizard |
 * | Elaboró | `useUser` (JWT) | Igual |
 * | Revisó | Placeholder producto | Igual |
 *
 * MFA (AAL2): generar desde snapshot post-submit no requiere nuevo GET.
 * Una descarga basada solo en servidor con PII en claro reutiliza
 * `GET /v1/captura/:parcela_id` (misma barrera que hidratación unmasked).
 */
import {
  parseGestionNotasFromColumn,
  type GestionNotasParsed,
  type ParsedGestionNotasBlocks,
} from '@aosenuma/lib-supabase';
import type { ReporteSeguimientoData } from './ReporteSeguimientoDocument';
import { NARRATIVA_VACIA, type VisitaNarrativa } from './visitaNarrativa';

export interface ParcelaReporteMeta {
  parcelaId: string;
  municipio: string;
  nomNucleo: string;
  numParcela: number | null;
  tipoPropiedadParcela: string | null;
  areaHa: number | null;
}

const SITUACION_LEGAL_LABEL: Record<string, string> = {
  libre: 'Libre de gravamen',
  hipoteca: 'Hipoteca activa',
  embargo: 'Embargo',
  litigio: 'En litigio',
  sucesion: 'Sucesión pendiente',
  otro: 'Otro',
};

const MODALIDAD_PAGO_LABEL: Record<string, string> = {
  anual: 'Anual',
  semestral: 'Semestral',
  mensual: 'Mensual',
};

const POSTURA_OPERATIVA_LABEL: Record<string, string> = {
  interesado: 'Interesado · dispuesto a firmar',
  en_analisis: 'En análisis · pendiente de decisión',
  neutral: 'Neutral · requiere más información',
  reticente: 'Reticente · con objeciones',
  rechaza: 'Rechaza la negociación',
};

const RIESGO_LABEL: Record<string, string> = {
  bajo: 'Bajo',
  medio: 'Medio',
  alto: 'Alto',
};

function emptyParsedBlocks(): ParsedGestionNotasBlocks {
  return {
    contrato: null,
    gestion: null,
    campo: null,
    narrativa: null,
    bono_firma_mxn: null,
    construccion: null,
    modelo_negocio_total_mxn: null,
    fallbackText: null,
  };
}

/** Convierte el shape del hook de hidratación en bloques del parser (sin re-parse JSON). */
export function parsedGestionToBlocks(note: GestionNotasParsed | null | undefined): ParsedGestionNotasBlocks {
  if (!note) return emptyParsedBlocks();
  return {
    contrato: note.contrato,
    gestion: note.gestion,
    campo: note.campo,
    narrativa: note.narrativa,
    bono_firma_mxn: note.bono_firma_mxn,
    construccion: note.construccion,
    modelo_negocio_total_mxn: note.modelo_negocio_total_mxn,
    fallbackText: note.notas_text,
  };
}

function mergeEsProp(siNo: boolean | null, campo: 'si' | 'no' | null | undefined): boolean | null {
  if (siNo === true || siNo === false) return siNo;
  if (campo === 'si') return true;
  if (campo === 'no') return false;
  return null;
}

function pickMerged(
  prefer: string | null | undefined,
  fallback: string | null | undefined,
): string | null {
  const p = typeof prefer === 'string' ? prefer.trim() : '';
  if (p !== '') return p;
  const f = typeof fallback === 'string' ? fallback.trim() : '';
  return f === '' ? null : f;
}

function narrativaMerged(
  fromNotas: ParsedGestionNotasBlocks['narrativa'],
  fallback: VisitaNarrativa | null | undefined,
): {
  descripcionVisita: string;
  descripcionNegociacion: string;
  reqsAreaLegal: string;
  observacionesTecnico: string;
} {
  const fb = fallback ?? NARRATIVA_VACIA;
  const n = fromNotas;
  return {
    descripcionVisita:
      pickMerged(n?.descripcion_visita ?? null, fb.descripcion_visita) ?? '',
    descripcionNegociacion:
      pickMerged(n?.descripcion_negociacion ?? null, fb.descripcion_negociacion) ?? '',
    reqsAreaLegal: pickMerged(n?.reqs_area_legal ?? null, fb.reqs_area_legal) ?? '',
    observacionesTecnico:
      pickMerged(n?.observaciones_tecnico ?? null, fb.observaciones_tecnico) ?? '',
  };
}

export interface ReporteSeguimientoBuildArgs {
  fechaGeneracion: string;
  elaboradoPor: string;
  parcela: ParcelaReporteMeta;

  /** PII / propietario (snapshot o servidor). */
  propietarioNombre: string | null;
  telefono: string | null;
  direccion: string | null;
  folioDerechos: string | null;
  folioTierras: string | null;
  claveCatastral: string | null;
  esElPropietario: boolean | null;
  signatarioNombre: string | null;
  terceroRelacion: string | null;
  calidadPosesionario: string | null;
  tipoTenencia: string | null;
  relacionEjido: string | null;
  liderazgoEjido: string | null;
  actaNacimientoFolio: string | null;

  estadoGestion: string;
  bonoFirmaMxn: number | null;
  plazoConstruccionAnios: number | null;

  /** `gestiones.notas` como string JSON (POST body o GET BFF). */
  notasRawJson?: string | null;
  /** Si ya viene parseado (hidratar RLS), evita stringify. */
  notasParsedHydrate?: GestionNotasParsed | null;

  /** Riesgo de columna gestiones cuando no está solo en JSON. */
  riesgo?: 'bajo' | 'medio' | 'alto' | null;

  /** Texto irregularidad principal (form o servidor). */
  irregularidadDetalle?: string | null;

  /** Última visita narrativa si no hay bloque embebido en notas. */
  visitaNarrativaFallback?: VisitaNarrativa | null;

  /** Leyendas de checklist completado (paso 3). */
  expedienteDocsCompletadosLabels?: string[];

  /** Coordenadas GPS del punto de visita, e.g. "20.12345, -100.67890". */
  coordenadasText?: string | null;
}

export function buildReporteSeguimientoDTO(args: ReporteSeguimientoBuildArgs): ReporteSeguimientoData {
  const blocks: ParsedGestionNotasBlocks =
    args.notasParsedHydrate != null
      ? parsedGestionToBlocks(args.notasParsedHydrate)
      : parseGestionNotasFromColumn(args.notasRawJson ?? null);

  const c = blocks.contrato;
  const g = blocks.gestion;
  const cmp = blocks.campo;

  const narr = narrativaMerged(blocks.narrativa, args.visitaNarrativaFallback);

  const sk = c?.situacion_legal ?? null;
  const situacionLegalLabel =
    sk && SITUACION_LEGAL_LABEL[sk] ? SITUACION_LEGAL_LABEL[sk] : sk ?? '—';

  const modalidadKey = c?.modalidad_pago ?? '';
  const modalidadPagoLabel =
    modalidadKey && MODALIDAD_PAGO_LABEL[modalidadKey] ? MODALIDAD_PAGO_LABEL[modalidadKey] : modalidadKey || '—';

  const posturaKey = g?.postura_propietario ?? '';
  const posturaOperativaLabel =
    posturaKey && POSTURA_OPERATIVA_LABEL[posturaKey]
      ? POSTURA_OPERATIVA_LABEL[posturaKey]
      : posturaKey || '—';

  const riesgoVal = args.riesgo ?? args.notasParsedHydrate?.riesgo ?? null;
  const riesgoLabel =
    riesgoVal && RIESGO_LABEL[riesgoVal] ? RIESGO_LABEL[riesgoVal] : '—';

  const bono = args.bonoFirmaMxn ?? blocks.bono_firma_mxn ?? null;
  const plazo = args.plazoConstruccionAnios ?? args.notasParsedHydrate?.plazo_anos ?? null;

  const esProp = mergeEsProp(args.esElPropietario, cmp?.es_el_propietario);

  const identificadorParcela =
    [
      args.parcela.parcelaId,
      args.folioDerechos ? `Folio derechos: ${args.folioDerechos}` : '',
      args.folioTierras ? `Folio tierras: ${args.folioTierras}` : '',
    ]
      .filter(Boolean)
      .join(' · ') || args.parcela.parcelaId;

  const expedienteResumen =
    args.expedienteDocsCompletadosLabels && args.expedienteDocsCompletadosLabels.length > 0
      ? args.expedienteDocsCompletadosLabels.join(', ')
      : '';

  return {
    parcelaId: args.parcela.parcelaId,
    numParcela: args.parcela.numParcela,
    nomNucleo: args.parcela.nomNucleo,
    municipio: args.parcela.municipio,
    tipoPropiedadParcela: args.parcela.tipoPropiedadParcela,
    areaHa: args.parcela.areaHa,

    propietarioNombre: args.propietarioNombre,
    telefono: args.telefono,
    direccion: args.direccion,

    esElPropietario: esProp,
    signatarioNombre: pickMerged(args.signatarioNombre, cmp?.signatario_nombre ?? null),
    terceroRelacion: pickMerged(args.terceroRelacion, cmp?.tercero_relacion ?? null),
    calidadPosesionario: pickMerged(args.calidadPosesionario, cmp?.calidad_posesionario ?? null),
    tipoTenencia: args.tipoTenencia,
    relacionEjido: pickMerged(args.relacionEjido, cmp?.relacion_ejido ?? null),
    liderazgoEjido: pickMerged(args.liderazgoEjido, cmp?.liderazgo_ejido ?? null),
    actaNacimientoFolio: pickMerged(args.actaNacimientoFolio, cmp?.acta_nacimiento_folio ?? null),
    folioDerechos: args.folioDerechos,
    folioTierras: args.folioTierras,
    claveCatastral: args.claveCatastral,

    estadoGestion: args.estadoGestion,
    bonoFirmaMxn: bono,
    plazoConstruccionAnios: plazo,

    descripcionVisita: narr.descripcionVisita,
    descripcionNegociacion: narr.descripcionNegociacion,
    reqsAreaLegal: narr.reqsAreaLegal,
    observacionesTecnico: narr.observacionesTecnico,

    fechaGeneracion: args.fechaGeneracion,
    elaboradoPor: args.elaboradoPor,

    colNorte: c?.colindancias.norte ?? null,
    colSur: c?.colindancias.sur ?? null,
    colOriente: c?.colindancias.oriente ?? null,
    colPoniente: c?.colindancias.poniente ?? null,
    situacionLegalLabel,
    riesgoLabel,
    modalidadPagoLabel,
    fechaFirmaEst: c?.fecha_firma_est ?? null,
    condiciones: c?.condiciones ?? null,
    posturaOperativaLabel,
    motivoInviable: g?.motivo_inviable ?? null,
    irregularidadDetalle: args.irregularidadDetalle?.trim() || null,
    expedienteDocsResumen: expedienteResumen || null,
    identificadorParcela,
    ubicacionNota: `Municipio: ${args.parcela.municipio}. Núcleo: ${args.parcela.nomNucleo}.`,
    coordenadasText: args.coordenadasText ?? null,
    areaArrendadaHa: null,
  };
}
